import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "components/SEO/SEO";
import { useDispatch, useSelector } from "react-redux";
import {
  MapPin,
  Calendar,
  Users,
  Clock,
  Star,
  Heart,
  ChevronRight,
  Utensils,
  Wine,
  RefreshCw,
  Building2,
  Play,
} from "lucide-react";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";
import {
  getAllExperiences,
  ExperienceSummary,
} from "../../redux/reducers/Experiences/ExperiencesSlice";
import { AppDispatch, RootState } from "../../redux/store";
import { huarazLake69 } from "../../images/experiences";
import ReactGA from "react-ga4";

// ─── Config ─────────────────────────────────────────────────────────────────

const EXPERIENCE_CATEGORIES = [
  "All",
  "Events",
  "Adventure",
  "Cultural",
  "Beach & Surf",
  "Food & Nightlife",
  "Sports",
  "Weekend Trips",
];

const FALLBACK_IMAGE =
  "https://images.pexels.com/photos/1271619/pexels-photo-1271619.jpeg";

const COMMUNITY_VIDEO =
  "https://res.cloudinary.com/delmdmex1/video/upload/v1778875960/huaraz-nomadroof-experiences-header-hero_qpxigr.mov";

// Height of your site header, so the filter bar sticks just below it.
// Adjust this to match the real header (Tailwind arbitrary value).
const STICKY_TOP = "top-[72px]";

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatTime = (timeStr: string | null): string => {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":");
  const hour = parseInt(h);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${m} ${ampm}`;
};

const getDateParts = (dateStr: string | null) => {
  if (!dateStr) return null;
  const date = new Date(dateStr + "T00:00:00");
  return {
    month: date.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
    day: date.getDate(),
    weekday: date.toLocaleDateString("en-US", { weekday: "short" }),
  };
};

const scrollToResults = () =>
  document
    .getElementById("category-section")
    ?.scrollIntoView({ behavior: "smooth" });

// ─── Main Page ──────────────────────────────────────────────────────────────

const ExperiencesPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { experiences, loading } = useSelector(
    (state: RootState) => state.experiences,
  );
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    dispatch(getAllExperiences());
    ReactGA.event({ category: "Page", action: "experiences_page_viewed" });
  }, [dispatch]);

  // Events live only in the "Events" tab; every other tab shows non-events.
  const filteredExperiences = (() => {
    if (activeCategory === "All") {
      return experiences.filter((exp: ExperienceSummary) => !exp.is_event);
    }
    if (activeCategory === "Events") {
      return experiences.filter((exp: ExperienceSummary) => exp.is_event);
    }
    return experiences.filter(
      (exp: ExperienceSummary) =>
        !exp.is_event && exp.category === activeCategory,
    );
  })();

  const upcomingEvents = experiences
    .filter((exp: ExperienceSummary) => exp.is_event)
    .slice(0, 3);

  const showEventsBand =
    upcomingEvents.length > 0 && activeCategory !== "Events";

  const openEvents = () => {
    setActiveCategory("Events");
    scrollToResults();
  };

  if (loading) {
    return (
      <div className="nc-ExperiencesPage min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#f5694b] mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">
            Loading experiences...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="nc-ExperiencesPage">
      <SEO
        title="Experiences in Lima, Peru"
        description="Discover unique student experiences, tours and activities in Lima, Peru. Book trips, city tours and cultural experiences through Nomadroof."
        canonical="/experiences"
      />
      <BgGlassmorphism />

      {/* ========== HERO (compact, split) ========== */}
      <section className="container relative px-4 mx-auto py-10 lg:py-16">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          <div className="text-center lg:text-left">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-widest text-[#e2583d]">
              For our exclusive community
            </span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 dark:text-white">
              Live Peru to the fullest
            </h1>
            <p className="mt-5 text-lg lg:text-xl text-gray-600 dark:text-gray-400 max-w-xl mx-auto lg:mx-0">
              Trips, events and local plans made for exchange students and young
              professionals.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <button
                onClick={scrollToResults}
                className="inline-flex items-center justify-center h-12 px-7 rounded-full bg-[#f5694b] hover:bg-[#e2583d] text-white font-semibold transition-colors"
              >
                Browse experiences
              </button>
              {upcomingEvents.length > 0 && (
                <button
                  onClick={openEvents}
                  className="inline-flex items-center justify-center h-12 px-7 rounded-full border-2 border-gray-900 dark:border-white text-gray-900 dark:text-white font-semibold hover:bg-gray-900 hover:text-white dark:hover:bg-white dark:hover:text-gray-900 transition-colors"
                >
                  See upcoming events
                </button>
              )}
            </div>
          </div>

          <div className="relative h-56 sm:h-72 lg:h-80 rounded-3xl overflow-hidden">
            <img
              src={huarazLake69}
              alt="Laguna 69 Huaraz Peru - Nomadroof Adventures"
              className="w-full h-full object-cover"
              style={{ objectPosition: "center 75%" }}
            />
          </div>
        </div>
      </section>

      {/* ========== STICKY FILTER BAR ========== */}
      <div
        id="category-section"
        className={`sticky ${STICKY_TOP} z-30 bg-white/95 dark:bg-neutral-900/95 backdrop-blur border-y border-gray-200 dark:border-neutral-800`}
      >
        <div className="container px-4 mx-auto">
          <div className="flex gap-2 overflow-x-auto py-3 scrollbar-hide">
            {EXPERIENCE_CATEGORIES.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`flex-shrink-0 h-11 px-5 rounded-full text-sm font-semibold whitespace-nowrap border transition-colors ${
                  activeCategory === category
                    ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-gray-900 dark:border-white"
                    : "bg-white dark:bg-neutral-800 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-neutral-700 hover:border-gray-900 dark:hover:border-white"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ========== UPCOMING EVENTS BAND ========== */}
      {showEventsBand && (
        <section className="bg-[#f5694b]/10 dark:bg-neutral-900 py-12 lg:py-14">
          <div className="container px-4 mx-auto">
            <div className="flex items-end justify-between mb-8">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-[#e2583d]">
                  Upcoming events
                </span>
                <h2 className="mt-2 text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                  Don't miss out
                </h2>
              </div>
              <button
                onClick={openEvents}
                className="text-sm font-semibold text-[#e2583d] hover:underline"
              >
                See all events →
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
              {upcomingEvents.map((event: ExperienceSummary) => (
                <EventRow key={event.id} experience={event} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========== RESULTS GRID ========== */}
      <section className="container px-4 mx-auto py-12 lg:py-16">
        <div className="flex items-end justify-between mb-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white">
            {activeCategory === "All"
              ? "All experiences"
              : activeCategory === "Events"
                ? "Events"
                : activeCategory}
          </h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {filteredExperiences.length}{" "}
            {filteredExperiences.length === 1 ? "result" : "results"}
          </span>
        </div>

        {filteredExperiences.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredExperiences.map((experience: ExperienceSummary) =>
              experience.is_event ? (
                <EventCard key={experience.id} experience={experience} />
              ) : (
                <ExperienceCard key={experience.id} experience={experience} />
              ),
            )}
          </div>
        ) : (
          <div className="text-center py-16 text-gray-500">
            <p>
              {activeCategory === "Events"
                ? "No upcoming events right now. Check back soon!"
                : "No experiences found in this category."}
            </p>
          </div>
        )}
      </section>

      {/* ========== COMMUNITY BAND ========== */}
      <section className="bg-gray-900 text-white py-12 lg:py-14">
        <div className="container px-4 mx-auto flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
          {/* Compact phone frame: fixed size so the video can never grow tall */}
          <div className="relative flex-shrink-0 w-[190px] h-[390px] lg:w-[250px] lg:h-[510px] lg:ml-24 rounded-[2.25rem] lg:rounded-[2.75rem] overflow-hidden border-[6px] border-gray-700 bg-gray-800 shadow-2xl">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 lg:w-24 h-4 lg:h-6 bg-gray-700 rounded-b-xl lg:rounded-b-2xl z-10" />
            <video
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
            >
              <source src={COMMUNITY_VIDEO} type="video/mp4" />
            </video>
          </div>
          <div className="text-center lg:text-left flex-1">
            <h2 className="text-3xl lg:text-5xl font-bold leading-tight">
              Meet people from 50+ countries
            </h2>
            <p className="mt-4 text-lg lg:text-xl text-gray-300 max-w-xl mx-auto lg:mx-0">
              Join group trips, match nights and local plans, curated for
              exchange students and young professionals.
            </p>
            <Link
              to="/community"
              onClick={() =>
                ReactGA.event({
                  category: "CTA",
                  action: "join_community_clicked",
                  label: "experiences_community_band",
                })
              }
              className="mt-8 inline-flex items-center gap-2 h-12 px-7 rounded-full bg-[#f5694b] hover:bg-[#e2583d] text-white font-semibold transition-colors"
            >
              <span>Join the community</span>
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ========== CTA ========== */}
      <section className="py-14 lg:py-20">
        <div className="container px-4 mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white">
            Not sure what to pick?
          </h2>
          <p className="mt-3 text-lg text-gray-600 dark:text-gray-400 max-w-xl mx-auto">
            Tell us what you're into and we'll point you to the right trip or
            event.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center gap-2 h-12 px-7 rounded-full bg-[#f5694b] hover:bg-[#e2583d] text-white font-semibold transition-colors"
          >
            <span>Get in touch</span>
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

// ─── Shared bits ────────────────────────────────────────────────────────────

const LikeButton: React.FC = () => {
  const [isLiked, setIsLiked] = useState(false);
  return (
    <button
      onClick={() => setIsLiked(!isLiked)}
      aria-label={isLiked ? "Remove from favourites" : "Save to favourites"}
      className="absolute top-3 right-3 w-11 h-11 bg-white rounded-full flex items-center justify-center shadow transition-transform hover:scale-105"
    >
      <Heart
        className={`w-5 h-5 ${isLiked ? "fill-[#f5694b] text-[#f5694b]" : "text-gray-800"}`}
      />
    </button>
  );
};

// ─── Event row (compact card in the events band) ────────────────────────────

const EventRow: React.FC<{ experience: ExperienceSummary }> = ({
  experience,
}) => {
  const parts = getDateParts(experience.event_date);

  return (
    <Link
      to={`/experiences/${experience.slug}`}
      className="group flex gap-4 p-4 rounded-2xl bg-white dark:bg-neutral-800 border border-[#f5694b]/20 dark:border-neutral-700 hover:shadow-lg transition-shadow"
    >
      {parts && (
        <div className="w-[72px] h-[88px] flex-shrink-0 rounded-xl bg-gray-900 text-white flex flex-col items-center justify-center">
          <span className="text-xs font-bold tracking-widest">
            {parts.month}
          </span>
          <span className="text-3xl font-bold leading-none mt-0.5">
            {parts.day}
          </span>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col justify-center gap-1">
        <h3 className="text-base font-bold text-gray-900 dark:text-white line-clamp-2 group-hover:text-[#e2583d] transition-colors">
          {experience.title}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
          {[
            experience.venue_name,
            experience.start_time &&
              `${formatTime(experience.start_time)}${
                experience.end_time ? ` – ${formatTime(experience.end_time)}` : ""
              }`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {experience.is_free ? (
            <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-green-100 text-green-800">
              Free
            </span>
          ) : experience.from_price ? (
            <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-800">
              From ${experience.from_price}
            </span>
          ) : null}
          {experience.food_included && (
            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700">
              Food
            </span>
          )}
          {experience.drinks_included && (
            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700">
              Drinks
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

// ─── Event card (grid card for the Events tab) ──────────────────────────────

const EventCard: React.FC<{ experience: ExperienceSummary }> = ({
  experience,
}) => {
  const parts = getDateParts(experience.event_date);

  return (
    <article className="group bg-white dark:bg-neutral-800 rounded-3xl overflow-hidden border border-gray-200 dark:border-neutral-700 hover:shadow-xl transition-shadow flex flex-col">
      <div className="relative h-56 overflow-hidden bg-gray-200">
        <img
          src={experience.cover_image || FALLBACK_IMAGE}
          alt={experience.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />

        {!experience.is_private && (experience.is_free || experience.from_price) && (
          <span
            className={`absolute top-4 left-4 text-xs font-bold px-3 py-1.5 rounded-full ${
              experience.is_free
                ? "bg-green-500 text-white"
                : "bg-white text-gray-900"
            }`}
          >
            {experience.is_free ? "FREE" : `From $${experience.from_price}`}
          </span>
        )}
        {experience.is_private && (
          <span className="absolute top-4 left-4 text-xs font-semibold px-3 py-1.5 rounded-full bg-gray-900 text-white">
            Private
          </span>
        )}
        <LikeButton />

        {parts && (
          <span className="absolute bottom-3 left-4 inline-flex items-center gap-1.5 bg-white text-gray-900 text-xs font-bold px-3 py-1.5 rounded-lg shadow">
            <Calendar className="w-3.5 h-3.5 text-[#e2583d]" />
            {parts.weekday}, {parts.month} {parts.day}
          </span>
        )}
      </div>

      <div className="p-5 flex flex-col gap-2 flex-1">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white line-clamp-2 group-hover:text-[#e2583d] transition-colors">
          {experience.title}
        </h3>

        {experience.venue_name && (
          <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
            <Building2 className="w-4 h-4 text-[#e2583d] flex-shrink-0" />
            <span className="truncate">{experience.venue_name}</span>
          </div>
        )}

        {experience.start_time && (
          <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
            <Clock className="w-4 h-4 text-[#e2583d] flex-shrink-0" />
            <span>
              {formatTime(experience.start_time)}
              {experience.end_time && ` – ${formatTime(experience.end_time)}`}
            </span>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mt-1">
          {experience.food_included && (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">
              <Utensils className="w-3 h-3" /> Food
            </span>
          )}
          {experience.drinks_included && (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">
              <Wine className="w-3 h-3" /> Drinks
            </span>
          )}
          {experience.is_recurring && (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">
              <RefreshCw className="w-3 h-3" />
              {experience.recurrence_rule || "Recurring"}
            </span>
          )}
          {experience.capacity && (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">
              <Users className="w-3 h-3" /> {experience.capacity} spots
            </span>
          )}
        </div>

        <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100 dark:border-neutral-700">
          <div className="text-sm font-semibold">
            {!experience.is_private &&
              (experience.is_free ? (
                <span className="text-green-600">Free entry</span>
              ) : experience.from_price ? (
                <span className="text-gray-500">
                  From{" "}
                  <span className="text-lg text-gray-900 dark:text-white">
                    ${experience.from_price}
                  </span>
                </span>
              ) : null)}
          </div>
          <Link
            to={`/experiences/${experience.slug}`}
            className="inline-flex items-center h-11 px-5 rounded-full bg-[#f5694b] hover:bg-[#e2583d] text-white text-sm font-semibold transition-colors"
          >
            {experience.show_booking_button
              ? experience.is_free
                ? "Register now"
                : "Book now"
              : "View details"}
          </Link>
        </div>
      </div>
    </article>
  );
};

// ─── Experience card (one size for everything) ──────────────────────────────

const ExperienceCard: React.FC<{ experience: ExperienceSummary }> = ({
  experience,
}) => {
  const nextDate = experience.next_dates?.[0] || "";

  return (
    <article className="group bg-white dark:bg-neutral-800 rounded-3xl overflow-hidden border border-gray-200 dark:border-neutral-700 hover:shadow-xl transition-shadow flex flex-col">
      <div className="relative h-56 overflow-hidden bg-gray-200">
        <img
          src={experience.cover_image || FALLBACK_IMAGE}
          alt={experience.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <span className="absolute top-4 left-4 bg-white text-gray-900 text-xs font-bold px-3 py-1.5 rounded-full shadow">
          {experience.category}
        </span>
        <LikeButton />
        {nextDate && (
          <span className="absolute bottom-3 left-4 inline-flex items-center gap-1.5 bg-white text-gray-900 text-xs font-bold px-3 py-1.5 rounded-lg shadow">
            <Calendar className="w-3.5 h-3.5 text-[#e2583d]" />
            {nextDate}
          </span>
        )}
      </div>

      <div className="p-5 flex flex-col gap-2 flex-1">
        <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
          <MapPin className="w-4 h-4 text-[#e2583d]" />
          <span>{experience.location}</span>
        </div>

        <h3 className="text-lg font-bold text-gray-900 dark:text-white line-clamp-2 group-hover:text-[#e2583d] transition-colors">
          {experience.title}
        </h3>

        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            {experience.duration}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Users className="w-4 h-4" />
            {experience.group_size}
          </span>
          {experience.rating ? (
            <span className="inline-flex items-center gap-1 ml-auto font-semibold text-gray-900 dark:text-white">
              <Star className="w-4 h-4 fill-[#f5694b] text-[#f5694b]" />
              {experience.rating}
              <span className="font-normal text-gray-500">
                ({experience.review_count})
              </span>
            </span>
          ) : null}
        </div>

        <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100 dark:border-neutral-700">
          <div className="text-sm text-gray-500">
            {experience.from_price ? (
              <>
                From{" "}
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  ${experience.from_price}
                </span>
              </>
            ) : null}
          </div>
          <Link
            to={`/experiences/${experience.slug}`}
            className="inline-flex items-center h-11 px-5 rounded-full bg-[#f5694b] hover:bg-[#e2583d] text-white text-sm font-semibold transition-colors"
          >
            View details
          </Link>
        </div>
      </div>
    </article>
  );
};

export default ExperiencesPage;